import { Box, Typography } from "@mui/material";
import type { ComponentPropsWithoutRef } from "react";
import { VideoPlayerProps } from "./types";
import { getEmbedUrl } from "./videoEmbed";

export const VideoPlayer = (props: VideoPlayerProps) => {
    const {
        src,
        width = "400px",
        height = "300px",
        alignment = "center",
        borderRadius = "8px",
        muted = false,
        loop = false,
        autoplay = false,
        poster,
    } = props;

    // Si aucune URL de vidéo n'est fournie, afficher un message informatif
    if (!src || src.trim() === "") {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems:
                        alignment === "center" ? "center"
                        : alignment === "right" ? "flex-end"
                        : "flex-start",
                }}
            >
                <Box
                    sx={{
                        width,
                        height,
                        borderRadius,
                        maxWidth: "100%",
                        backgroundColor: "#f5f5f5",
                        border: "2px dashed #ccc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: 1,
                    }}
                >
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                        📹
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                        t("puck.video_player.no_video")
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems:
                    alignment === "center" ? "center"
                    : alignment === "right" ? "flex-end"
                    : "flex-start",
            }}
        >
            {(() => {
                const normalizeFlag = (v: boolean | string | undefined) => v === true || v === "true";

                // YouTube / Vimeo link → embedded player. Poster is ignored (the provider shows its own thumbnail).
                const embedUrl = getEmbedUrl(src, {
                    muted: normalizeFlag(muted),
                    loop: normalizeFlag(loop),
                    autoplay: normalizeFlag(autoplay),
                });
                if (embedUrl) {
                    return (
                        <Box
                            component="iframe"
                            src={embedUrl}
                            title="Embedded video player"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                            sx={{
                                width,
                                height,
                                borderRadius,
                                maxWidth: "100%",
                                border: 0,
                                backgroundColor: "#000",
                            }}
                        />
                    );
                }

                const videoProps: ComponentPropsWithoutRef<"video"> = {
                    src,
                    poster,
                    controls: true,
                    muted: normalizeFlag(muted),
                    loop: normalizeFlag(loop),
                    autoPlay: normalizeFlag(autoplay),
                };
                return (
                    <Box
                        component="video"
                        {...videoProps}
                        sx={{
                            width,
                            height,
                            borderRadius,
                            maxWidth: "100%",
                            backgroundColor: "#000",
                        }}
                    />
                );
            })()}
        </Box>
    );
};
